import React, { useState } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { PermissionChip} from "../../share";

import {
  Avatar, 
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardActions,
  CardContent as MuiCardContent,
  CardMedia as MuiCardMedia,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
} from "@mui/material";

import { AvatarGroup as MuiAvatarGroup } from "@mui/lab";

import { red, green, orange, yellow } from "@mui/material/colors";

import { spacing } from "@mui/system";
import { Translate } from "react-localize-redux";
import { ColourChip } from "../../../general/mini_components-ts";
import CopyInvestigationModal from "../../copy/CopyInvestigationModal";
import { PERMISSION } from "../../share/user_roles";
import { copyInvestigationService } from "../../../../services/investigation";
import { generateKey, encryptData } from "../../../../utils/index.jsx";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)`
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[300]};
`;

const CardMedia = styled(MuiCardMedia)`
  height: 220px;
`;

const Divider = styled(MuiDivider)(spacing);

const Typography = styled(MuiTypography)(spacing);

const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) => props.rgbcolor};
  color: ${(props) => props.theme.palette.common.white};
  margin-bottom: ${(props) => props.theme.spacing(4)};
  margin-right:1rem;
`;

const AvatarGroup = styled(MuiAvatarGroup)`
  margin-left: ${(props) => props.theme.spacing(2)};
`;

export default function CardInvestigation({ image, title, description, status, shareStatus, uuid, hostResearcher, answerRequest, index, permissions, assignedRole }) {
    const history = useHistory();
    // SHER-576: the copy action is gated on the granular COPY_INVESTIGATION permission
    // for THIS investigation (permissions is the per-investigation array from the API).
    const canCopy = Array.isArray(permissions) && permissions.includes(PERMISSION.COPY_INVESTIGATION);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copying, setCopying] = useState(false);
    const [copyError, setCopyError] = useState(false);

    async function handleCopy(payload){
        setCopying(true);
        setCopyError(false);
        try{
            // A copy needs its OWN investigation key (fresh, encrypted under the
            // researcher's raw key) — mirrors the create flow. The API stays
            // zero-knowledge; nothing here decrypts the source.
            const rawKeyResearcherInvestigation = await generateKey();
            const keyResearcherInvestigation = encryptData(rawKeyResearcherInvestigation, localStorage.getItem("rawKeyResearcher"));
            const response = await copyInvestigationService(uuid, { ...payload, keyResearcherInvestigation });
            setCopyModalOpen(false);
            history.push("/investigation/show/" + response.investigation.uuid);
        }
        catch(err){
            setCopying(false);
            setCopyError(true);
        }
    }
    const chip = () => {
        switch(status){
            case 0:
                return <Chip label="Draft" rgbcolor={yellow[500]} />
            case 1:
                return <Chip label="Live" rgbcolor={green[500]} />
            default:
                return <Chip label="Draft" rgbcolor={yellow[500]} />
        }
        
    }
    const chipStatus = () => {
        if(shareStatus === 0){
            return <ColourChip rgbcolor={orange[500]} label="Pending"/>
        } 
        else{
            return null;
        }
        
    }
    
    function renderCardActions(){
        const copyButton = canCopy ? (
            <Button data-testid="copy-investigation" size="small" color="primary" onClick={() => { setCopyError(false); setCopyModalOpen(true); }}>
                <Translate id="investigation.copy.action" />
            </Button>
        ) : null;
        if(shareStatus === 0){
            return(
                <CardActions>
                    <Button data-testid="deny" size="small" color="primary" onClick={() =>answerRequest(index, 1)}>
                        Deny
                    </Button>
                    <Button data-testid="accept" size="small" color="secondary" onClick={() => answerRequest(index, 2)}>
                        Accept
                    </Button>
                </CardActions>
                
            )
        }
        else{
            if(status === 0){
                return(
                <CardActions>
                    <NavLink to={`/investigation/edit/${uuid}`}>
                        <Button data-testid="open" size="small" color="secondary">
                            Edit
                        </Button>
                    </NavLink>
                    {
                        permissions > 2 &&
                        <NavLink to={`/investigation/share/${uuid}`} >
                            <Button data-testid="share" size="small" color="primary">
                                Share
                            </Button>
                        </NavLink>
                    }
                    {copyButton}
                </CardActions>
                )
            }
            else{
                return(
                    <CardActions>
                        {
                            permissions > 2 &&
                            <NavLink to={`/investigation/share/${uuid}`} >
                                <Button data-testid="share" size="small" color="primary">
                                    Share
                                </Button>
                            </NavLink>
                        }
                        
                            <NavLink to={`/investigation/show/${uuid}`}>
                                <Button data-testid="open" size="small" color="secondary">
                                    Open
                                </Button>
                            </NavLink>
                        {copyButton}
                    </CardActions>
                )
            }
            
        }
    }
    return (
        <>
        <Card className="investigation" mb={6}>
        {image ? <CardMedia image={image} title="Contemplative Reptile" /> : null}
        <CardContent>
            <Grid container>
                <Grid item xs={12}>
                    <Typography gutterBottom variant="h5" component="h2">
                    {title}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {/* SHER-576: show the REAL assigned role the API reports, routed through
                        PermissionChip (translated + colour-coded). The old value-only path
                        guessed the role by exact-matching the permission set, which shows
                        "No role" whenever the client's role arrays drift from the API's (e.g.
                        after COPY_INVESTIGATION was added). A null assignedRole (no role) is
                        passed with empty permissions so PermissionChip renders its translated
                        NO_ROLE_ASSIGNED chip instead of guessing. Fall back to the value path
                        only when the API build predates assignedRole (undefined). */}
                    Role: {assignedRole !== undefined
                        ? <PermissionChip role={assignedRole ? { name: assignedRole } : undefined} value={[]} />
                        : <PermissionChip value={permissions} />}

                </Grid>
                {
                shareStatus === 0 && 
                    <Grid item xs={12}>
                        <Typography variant="caption">
                            <Translate id="investigation.share.shared-by" />{hostResearcher.name} {hostResearcher.surnames}
                        </Typography>
                    </Grid>
                }
                <Grid item xs={12}>
                    <Typography mb={4} component="p" dangerouslySetInnerHTML={{__html:description}} />
                </Grid>
            </Grid>            
            {/* <AvatarGroup max={3}>
                <Avatar alt="Avatar" src="/static/img/avatars/avatar-1.jpg" />
                <Avatar alt="Avatar" src="/static/img/avatars/avatar-2.jpg" />
                <Avatar alt="Avatar" src="/static/img/avatars/avatar-3.jpg" />
            </AvatarGroup> */}
        </CardContent>
        {
            renderCardActions()
        }
        </Card>
        {
            canCopy &&
            <CopyInvestigationModal
                open={copyModalOpen}
                onClose={() => setCopyModalOpen(false)}
                onSubmit={handleCopy}
                loading={copying}
                error={copyError ? <Translate id="investigation.copy.error" /> : null}
                defaultName={"Copy of " + title}
            />
        }
        </>
    );
}
